import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { SHOP_NAMES } from '../gen/buildings/details';

/**
 * Geometry and materials of the building detail instances (placed in buildingMesh.ts):
 * - windowTrim: stone sill and lintel around a row-house window (metres, scale 1; the frame is the
 *   window's origin: x along the wall, y up from the opening's bottom, z out of the wall);
 * - roofBox: a unit box (x, y, z in [0, 1]) for AC units, vents, chimneys, mechanical rooms, antennas,
 *   coloured per instance;
 * - sign: a shop sign board; instanceColor.x picks the name from a canvas atlas;
 * - awning: a sloped canopy over a shop window or door, coloured per instance.
 */

function box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, color: THREE.Color): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(x1 - x0, y1 - y0, z1 - z0).translate((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2).toNonIndexed();
  g.deleteAttribute('uv');
  const n = g.getAttribute('position').count;
  const c = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) color.toArray(c, i * 3);
  g.setAttribute('color', new THREE.BufferAttribute(c, 3));
  return g;
}

export function windowTrimGeometry(width: number, height: number): THREE.BufferGeometry {
  const stone = new THREE.Color(0.86, 0.84, 0.79);
  return mergeGeometries([
    box(-0.06, -0.07, 0, width + 0.06, 0, 0.08, stone), // sill
    box(-0.08, height, 0, width + 0.08, height + 0.14, 0.03, stone), // lintel
  ])!;
}

export function roofBoxGeometry(): THREE.BufferGeometry {
  return new THREE.BoxGeometry(1, 1, 1).translate(0.5, 0.5, 0.5);
}

/** Awning: a canopy sloping down and out from the wall, open underneath, in unit size (scaled per instance). */
export function awningGeometry(): THREE.BufferGeometry {
  // Top at the wall (y = 1), front edge 0.35 lower and 1 unit out; valance 0.12 deep at the front.
  // Drawn double-sided, so it reads from below too.
  const g = new THREE.BufferGeometry();
  const v = [
    // Sloped top (two triangles), facing up and out.
    0, 1, 0, 1, 1, 0, 1, 0.65, 1, 0, 1, 0, 1, 0.65, 1, 0, 0.65, 1,
    // Front valance.
    0, 0.65, 1, 1, 0.65, 1, 1, 0.53, 1, 0, 0.65, 1, 1, 0.53, 1, 0, 0.53, 1,
    // Sides.
    0, 1, 0, 0, 0.65, 1, 0, 0.53, 1, 1, 1, 0, 1, 0.53, 1, 1, 0.65, 1,
  ];
  g.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
  g.computeVertexNormals();
  return g;
}

export function signGeometry(): THREE.BufferGeometry {
  // A board 1 x 1 in the wall's plane, 6 cm deep; uv (0..1) on the front face only.
  const g = new THREE.BoxGeometry(1, 1, 0.06).translate(0.5, 0.5, 0.03);
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  const nrm = g.getAttribute('normal') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) if (nrm.getZ(i) < 0.5) uv.setXY(i, 0.02, 0.02); // edges: the board colour
  return g;
}

const COLS = 4;
const ROWS = 8;

/** Sign atlas: COLS × ROWS boards, each a shop name in white or black on a coloured board. */
function signAtlas(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 512;
  const g = c.getContext('2d')!;
  const w = c.width / COLS;
  const h = c.height / ROWS;
  const boards = ['#7a1f1f', '#1f3f7a', '#1f5a33', '#222222', '#6b4e1f', '#e8e1cf', '#8a2d5a', '#b36b00'];
  SHOP_NAMES.slice(0, COLS * ROWS).forEach((name, i) => {
    const x = (i % COLS) * w;
    const y = Math.floor(i / COLS) * h;
    const bg = boards[i % boards.length];
    g.fillStyle = bg;
    g.fillRect(x, y, w, h);
    g.strokeStyle = 'rgba(255,255,255,0.35)';
    g.lineWidth = 3;
    g.strokeRect(x + 4, y + 4, w - 8, h - 8);
    g.fillStyle = bg === '#e8e1cf' ? '#222' : '#f4efe2';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    let size = 40;
    g.font = `bold ${size}px sans-serif`;
    while (g.measureText(name).width > w - 24 && size > 12) g.font = `bold ${(size -= 2)}px sans-serif`;
    g.fillText(name, x + w / 2, y + h / 2 + 2);
  });
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** Sign material: the board's atlas cell from instanceColor.x (the name index). */
export function signMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ map: signAtlas(), roughness: 0.6 });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace(
      '#include <uv_vertex>',
      `#include <uv_vertex>
      #ifdef USE_INSTANCING_COLOR
        float signIdx = floor(instanceColor.x + 0.5);
      #else
        float signIdx = 0.0;
      #endif
      vMapUv = (vec2(mod(signIdx, ${COLS}.0), ${ROWS - 1}.0 - floor(signIdx / ${COLS}.0)) + vMapUv) / vec2(${COLS}.0, ${ROWS}.0);`,
    );
    // instanceColor carries data, not a tint.
    sh.fragmentShader = sh.fragmentShader.replace('#include <color_fragment>', '');
  };
  m.customProgramCacheKey = () => 'shopSign';
  return m;
}
